import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Building2, Loader2, CheckCircle2, XCircle, MapPin } from "lucide-react";

export default function OfficesPage() {
  const [offices, setOffices] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/offices");
      setOffices(data);
    } catch (e) {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (o) => {
    try {
      await api.put(`/offices/${o.id}`, { active: !o.active });
      toast.success(o.active ? "Sede disattivata" : "Sede attivata");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="offices-page">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Gestione Sedi</h1>
        <p className="mt-1 text-sm text-slate-500">Le quattro sedi operative di ESSECI E-LIGHT.</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {offices.map((o) => (
            <Card key={o.id} className="flex items-center justify-between border-slate-200 p-5" data-testid={`office-card-${o.id}`}>
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-sky-50 text-sky-600">
                  <Building2 className="h-5 w-5" />
                </div>
                <div>
                  <div className="font-display font-semibold text-slate-900">{o.name}</div>
                  <div className="mt-0.5 flex items-center gap-1 text-xs text-slate-500">
                    <MapPin className="h-3 w-3" /> Sede operativa
                  </div>
                  <div className="mt-1.5">
                    {o.active ? (
                      <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-800">
                        <CheckCircle2 className="h-3 w-3" /> Attiva
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-xs font-semibold text-rose-800">
                        <XCircle className="h-3 w-3" /> Non attiva
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <Switch checked={o.active} onCheckedChange={() => toggle(o)} data-testid={`toggle-office-${o.id}`} />
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
